// Retailer portal — branding loader + applier.
//
// Fetches the calling retailer's brand profile from
// `/api/portal/retailer-profile` and applies it as CSS variables on
// `:root`. The single token everything in the retailer portal already
// uses for accents is `--color-amber` (defined in
// packages/shared-ui/tokens.css). Overriding that one variable at the
// document level swaps every accent — buttons, active nav, focus rings,
// progress, badges — without touching hundreds of CSS files.
//
// Also exposes the resolved brand for the header (business name + logo)
// and listens for `tkos:branding-changed` so a save in Settings →
// Branding reflects immediately without a page reload.
//
// Single-tenant note: in this staging instance one retailer logs in at
// a time; the loader runs once at shell-mount and refreshes only when
// the retailer hits Save (or every 5 minutes via stale-while-revalidate
// behaviour on the server cache).

import { api, ApiError } from "@tkos/api-client";

/** The platform's own brand name. Single source of truth — the header's
 *  "is this the platform default?" branch reads `usingPlatformDefault`
 *  rather than string-comparing against this, so renaming the product can
 *  never silently change rendering behaviour. */
export const PLATFORM_BRAND_NAME = "Kitchengine";

export interface RetailerBranding {
  id: string;
  businessName: string;
  logoUrl: string;       // empty string = no logo set
  primaryColour: string; // "#rrggbb"
  secondaryColour: string;
  /** True when the server supplied no business name and we fell back to
   *  PLATFORM_BRAND_NAME — i.e. pre-auth, a fetch in flight, or a fresh
   *  retailer who hasn't set one. Consumers that need to distinguish
   *  "platform default" from "a retailer genuinely named X" MUST read this
   *  flag; a retailer is free to call themselves anything, including the
   *  platform name, so comparing the string is not a sound test. */
  usingPlatformDefault: boolean;
}

const DEFAULT_BRANDING: RetailerBranding = {
  id: "",
  // Fallback name so a fresh retailer with no business_name set doesn't get
  // a blank header.
  businessName: PLATFORM_BRAND_NAME,
  logoUrl: "",
  primaryColour: "#E8760A",  // shared platform empty-state brand colour (2026-06-10)
  secondaryColour: "#1F2937",
  usingPlatformDefault: true,
};

let _current: RetailerBranding = { ...DEFAULT_BRANDING };
const _subscribers = new Set<(b: RetailerBranding) => void>();

/** Internal: read the wire shape /retailer-profile returns and clip
 *  to the fields we care about. Anything missing falls back to the
 *  TKOS default values so a partial server response can't render a
 *  blank header. */
interface ProfileResponse {
  ok?: boolean;
  profile?: {
    id?: string;
    businessName?: string;
    logoUrl?: string;
    primaryColour?: string;
    secondaryColour?: string;
  };
}

function pick(b: ProfileResponse | null): RetailerBranding {
  const p = (b && b.profile) || {};
  const suppliedName = typeof p.businessName === "string" && p.businessName.trim();
  return {
    id:              typeof p.id === "string" ? p.id : DEFAULT_BRANDING.id,
    businessName:    suppliedName || DEFAULT_BRANDING.businessName,
    usingPlatformDefault: !suppliedName,
    logoUrl:         typeof p.logoUrl === "string" ? p.logoUrl : DEFAULT_BRANDING.logoUrl,
    primaryColour:   (typeof p.primaryColour === "string" && /^#[0-9a-fA-F]{6}$/.test(p.primaryColour))
                       ? p.primaryColour
                       : DEFAULT_BRANDING.primaryColour,
    secondaryColour: (typeof p.secondaryColour === "string" && /^#[0-9a-fA-F]{6}$/.test(p.secondaryColour))
                       ? p.secondaryColour
                       : DEFAULT_BRANDING.secondaryColour,
  };
}

/** Apply the resolved branding to the document. The retailer portal's
 *  entire token system is built on `--color-amber` so overriding it
 *  here ripples through every accent automatically. We also set
 *  `--brand-primary` and `--brand-secondary` for parity with the
 *  customer portal (and any new code that opts into the cross-portal
 *  naming). Derived tokens (--color-amber-glow / -ring) remain as
 *  rgba constants — they're subtle tints; a small visual delta during
 *  recolour is acceptable for v1. */
function applyToRoot(b: RetailerBranding): void {
  const root = document.documentElement;
  // Single-source override — every existing var(--color-amber)
  // reference picks up the new colour instantly.
  root.style.setProperty("--color-amber", b.primaryColour);
  // Mirror under the cross-portal naming convention so future shared
  // components only need to read --brand-primary.
  root.style.setProperty("--brand-primary", b.primaryColour);
  root.style.setProperty("--brand-secondary", b.secondaryColour);
}

/** Public: the currently applied branding (synchronous). */
export function getBranding(): RetailerBranding {
  return _current;
}

/** Public: subscribe to changes (header re-renders on event). */
export function onBrandingChange(fn: (b: RetailerBranding) => void): () => void {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

function notify(): void {
  for (const fn of _subscribers) {
    try {
      fn(_current);
    } catch (err) {
      console.warn("[branding] subscriber threw", err);
    }
  }
}

/** Fetch from the server and apply. Idempotent — call freely. Silent on
 *  network / 401 / 503 errors so a backend hiccup doesn't blank the UI;
 *  the existing TKOS defaults stay applied. */
export async function refreshBranding(signal?: AbortSignal): Promise<RetailerBranding> {
  try {
    const init = signal ? { signal } : undefined;
    const resp = await api<ProfileResponse>("/portal/retailer-profile", init);
    _current = pick(resp);
    applyToRoot(_current);
    notify();
    return _current;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 503 || err.status === 404)) {
      // Pre-auth or no-row — keep defaults applied.
      applyToRoot(_current);
      return _current;
    }
    console.warn("[branding] refresh failed; keeping defaults", err);
    applyToRoot(_current);
    return _current;
  }
}

/** Listen for the `tkos:branding-changed` CustomEvent dispatched by
 *  Settings → Branding after a successful save. Returns an `off()`. */
export function startBrandingListener(): () => void {
  const handler = (): void => {
    void refreshBranding();
  };
  window.addEventListener("tkos:branding-changed", handler);
  return () => window.removeEventListener("tkos:branding-changed", handler);
}

/** Convenience for the Settings page after a successful PUT. */
export function dispatchBrandingChanged(): void {
  window.dispatchEvent(new CustomEvent("tkos:branding-changed"));
}
